import type {
  CompatibilityIntakeFormData,
  CompatibilityPersonFormData,
} from './compatibilityIntake';
import type { SavedCompatibilityProfile } from '../store';

export function buildSavedCompatibilityProfileLabel(
  profile: Pick<SavedCompatibilityProfile, 'name' | 'birthDate' | 'birthTime' | 'birthTimeUnknown' | 'placeName'>,
): string {
  const datePart = profile.birthDate || 'unknown date';
  const timePart = profile.birthTimeUnknown ? 'time unknown' : profile.birthTime || 'unknown time';
  const placePart = profile.placeName || 'unknown place';
  return `${profile.name} · ${datePart} · ${timePart} · ${placePart}`;
}

export function projectSavedProfileToPerson(
  profile: SavedCompatibilityProfile,
): Partial<CompatibilityPersonFormData> {
  return {
    name: profile.name,
    birthDate: profile.birthDate,
    birthTime: profile.birthTime,
    birthTimeUnknown: profile.birthTimeUnknown ?? false,
    placeName: profile.placeName,
    latitude: profile.latitude,
    longitude: profile.longitude,
    timezoneOffset: profile.timezoneOffset,
    gender: profile.gender,
    selfOrientation: profile.selfOrientation ?? 'unsure',
    familyOrientation: profile.familyOrientation ?? 'unknown',
    decisionStyle: profile.decisionStyle ?? 'unsure',
    livingPreference: profile.livingPreference ?? 'undecided',
    careerPriority: profile.careerPriority ?? 'undecided',
    ritualPractice: profile.ritualPractice ?? 'mixed',
    socialBoundaryComfort: profile.socialBoundaryComfort ?? '',
  };
}

export function buildSavedProfileFromPerson(
  person: CompatibilityPersonFormData,
): Omit<SavedCompatibilityProfile, 'id' | 'savedAt' | 'updatedAt'> | null {
  const name = person.name.trim();
  if (!name) return null;
  return {
    name,
    birthDate: person.birthDate,
    birthTime: person.birthTime,
    birthTimeUnknown: person.birthTimeUnknown,
    placeName: person.placeName,
    latitude: person.latitude,
    longitude: person.longitude,
    timezoneOffset: person.timezoneOffset,
    gender: person.gender,
    selfOrientation: person.selfOrientation,
    familyOrientation: person.familyOrientation,
    decisionStyle: person.decisionStyle,
    livingPreference: person.livingPreference,
    careerPriority: person.careerPriority,
    ritualPractice: person.ritualPractice,
    socialBoundaryComfort: person.socialBoundaryComfort,
  };
}

function normalizeProfileKeyPart(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function findMatchingSavedCompatibilityProfile(
  profiles: SavedCompatibilityProfile[],
  person:
    | Partial<Pick<CompatibilityPersonFormData, 'name' | 'birthDate' | 'birthTime'>>
    | null
    | undefined,
): SavedCompatibilityProfile | null {
  if (!person) return null;
  const name = normalizeProfileKeyPart(person.name ?? '');
  const birthDate = (person.birthDate ?? '').trim();
  const birthTime = (person.birthTime ?? '').trim();
  if (!name || !birthDate || !birthTime) return null;
  return (
    profiles.find(
      (profile) =>
        normalizeProfileKeyPart(profile.name) === name &&
        profile.birthDate.trim() === birthDate &&
        profile.birthTime.trim() === birthTime,
    ) ?? null
  );
}

/**
 * Build a partner-library save payload from a Person 2 form, reusing the
 * id of an existing profile when the same person was already saved.
 * This prevents duplicate cards when the user manually re-enters a
 * partner or selects them from the library before saving.
 *
 * Comparison metadata (lastComparedAt / lastReportArtifactId) can be
 * forwarded to preserve or update the partner's report history.
 */
export function buildPartnerSavePayload(
  person: CompatibilityPersonFormData,
  savedProfiles: SavedCompatibilityProfile[],
  currentPartnerId?: string | null,
  comparisonMetadata?: {
    lastComparedAt?: string | null;
    lastReportArtifactId?: string | null;
  },
): (Omit<SavedCompatibilityProfile, 'id' | 'savedAt' | 'updatedAt'> & { id?: string }) | null {
  const base = buildSavedProfileFromPerson(person);
  if (!base) return null;

  const existingById = currentPartnerId
    ? savedProfiles.find((p) => p.id === currentPartnerId)
    : undefined;
  const existingByDetails = findMatchingSavedCompatibilityProfile(savedProfiles, person);
  const existing = existingById ?? existingByDetails;

  return {
    ...base,
    id: existing?.id,
    lastComparedAt: comparisonMetadata?.lastComparedAt ?? existing?.lastComparedAt ?? null,
    lastReportArtifactId: comparisonMetadata?.lastReportArtifactId ?? existing?.lastReportArtifactId ?? null,
  };
}

export function applySavedProfileToForm(
  form: CompatibilityIntakeFormData,
  personKey: 'person1' | 'person2',
  profile: SavedCompatibilityProfile,
): CompatibilityIntakeFormData {
  return {
    ...form,
    [personKey]: {
      ...form[personKey],
      ...projectSavedProfileToPerson(profile),
    },
  };
}

export function buildChatComparisonContext(
  primary: SavedCompatibilityProfile,
  comparison: SavedCompatibilityProfile,
  scope?: { contextId: string; threadTitle?: string },
): Record<string, unknown> {
  return {
    context_id: scope?.contextId,
    thread_title: scope?.threadTitle,
    comparison_mode: 'saved_people',
    comparison_source: 'saved_profiles',
    person1_name: primary.name,
    person2_name: comparison.name,
    comparison_note:
      'No deterministic compatibility report is attached. Use these saved birth details for a qualitative comparison and be explicit that no computed score was generated in chat.',
    compare_people: [
      {
        role: 'primary',
        name: primary.name,
        birth_date: primary.birthDate,
        birth_time: primary.birthTime,
        birth_time_precision_minutes: primary.birthTimeUnknown ? 720 : undefined,
        place_name: primary.placeName,
        latitude: primary.latitude,
        longitude: primary.longitude,
        timezone_offset: primary.timezoneOffset,
      },
      {
        role: 'comparison',
        name: comparison.name,
        birth_date: comparison.birthDate,
        birth_time: comparison.birthTime,
        birth_time_precision_minutes: comparison.birthTimeUnknown ? 720 : undefined,
        place_name: comparison.placeName,
        latitude: comparison.latitude,
        longitude: comparison.longitude,
        timezone_offset: comparison.timezoneOffset,
      },
    ],
  };
}
